import type { PageItem, PageItemMeta } from '@/db/content';
import { getUrlForItem } from '@/navTree';

export const LOCAL_PAGE_SEARCH_MAX_RESULTS = 10;

/** How many ranked hits to consider before collapsing translations (keeps top groups diverse). */
export const LOCAL_PAGE_SEARCH_RANK_POOL = 80;

/** Visible/plain text for display and matching (drops simple HTML tags from API titles). */
export function stripHtmlTags(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function normalizeSearchQuery(raw: string): string {
  return stripHtmlTags(raw).toLowerCase().trim();
}

export interface RankedPageMatch {
  item: PageItem;
  score: number;
}

function metaString(m: PageItemMeta | undefined, key: keyof PageItemMeta): string {
  const v = m?.[key];
  return typeof v === 'string' ? v : '';
}

/**
 * Higher = better. Zero = no match.
 * Order intent: title prefix > title contains > seo_title > search_description > slug.
 */
export function scorePageItemMatch(item: PageItem, normalizedQuery: string): number {
  if (!normalizedQuery) return 0;
  const q = normalizedQuery;
  const title = stripHtmlTags(typeof item.title === 'string' ? item.title : '').toLowerCase();
  if (!title && !item.meta) return 0;

  let score = 0;
  if (title.startsWith(q)) score = Math.max(score, 1000);
  else if (title.includes(q)) score = Math.max(score, 500);

  const seo = metaString(item.meta, 'seo_title').toLowerCase();
  if (seo.includes(q)) score = Math.max(score, 300);

  const desc = metaString(item.meta, 'search_description').toLowerCase();
  if (desc.includes(q)) score = Math.max(score, 200);

  const slug = metaString(item.meta, 'slug').toLowerCase();
  if (slug.includes(q)) score = Math.max(score, 100);

  return score;
}

/** All locales; caller builds URLs. Sorted by score desc, then title. */
export function rankPageMatches(items: PageItem[], rawQuery: string): RankedPageMatch[] {
  const q = normalizeSearchQuery(rawQuery);
  if (!q) return [];

  const out: RankedPageMatch[] = [];
  for (const item of items) {
    const locale = item.meta?.locale;
    if (!locale) continue;
    const score = scorePageItemMatch(item, q);
    if (score > 0) out.push({ item, score });
  }

  out.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return stripHtmlTags(a.item.title).localeCompare(stripHtmlTags(b.item.title), undefined, {
      sensitivity: 'base',
    });
  });
  return out;
}

export interface LocalPageSearchTranslation {
  locale: string;
  url: string;
}

export interface LocalPageSearchGroup {
  groupKey: string;
  title: string;
  primaryUrl: string;
  primaryLocale: string;
  /** Left stack: UI locale first, then tet, then others by locale code. */
  translations: LocalPageSearchTranslation[];
  maxScore: number;
}

interface GroupMember {
  score: number;
  url: string;
  locale: string;
  title: string;
  rankIndex: number;
}

function translationGroupKey(item: PageItem): string {
  const tk = item.meta?.translation_key;
  return typeof tk === 'string' && tk.trim().length > 0 ? tk : `__id:${item.id}`;
}

/** Stack order: active UI locale, then tet, then remaining locales A–Z. */
export function compareLocalesForSearchStack(a: string, b: string, uiLocale: string): number {
  const order = (loc: string): number => {
    if (loc === uiLocale) return 0;
    if (loc === 'tet') return 1;
    return 2;
  };
  const da = order(a);
  const db = order(b);
  if (da !== db) return da - db;
  return a.localeCompare(b);
}

/** Dedupe by locale keeping the better-scored row. */
function mergeMembersByLocale(members: GroupMember[]): GroupMember[] {
  const byLocale = new Map<string, GroupMember>();
  for (const m of members) {
    const prev = byLocale.get(m.locale);
    if (!prev || m.score > prev.score || (m.score === prev.score && m.rankIndex < prev.rankIndex)) {
      byLocale.set(m.locale, m);
    }
  }
  return [...byLocale.values()];
}

function pickPrimaryMember(members: GroupMember[], uiLocale: string): GroupMember {
  const byUi = members.find((m) => m.locale === uiLocale);
  if (byUi) return byUi;
  const byTet = members.find((m) => m.locale === 'tet');
  if (byTet) return byTet;
  return [...members].sort((a, b) => a.locale.localeCompare(b.locale))[0]!;
}

/**
 * Collapse ranked hits that share Wagtail `translation_key` into one row.
 * Primary link: `uiLocale` if present, else tet, else lexicographically first locale.
 */
export function groupRankedPageMatches(
  ranked: RankedPageMatch[],
  rootPathByLocale: Map<string, string>,
  defaultLocale: string,
  uiLocale: string,
  maxGroups: number = LOCAL_PAGE_SEARCH_MAX_RESULTS
): LocalPageSearchGroup[] {
  const pool = ranked.slice(0, LOCAL_PAGE_SEARCH_RANK_POOL);
  const groupMap = new Map<string, GroupMember[]>();

  for (let rankIndex = 0; rankIndex < pool.length; rankIndex++) {
    const { item, score } = pool[rankIndex]!;
    const locale = item.meta?.locale;
    if (!locale) continue;
    const key = translationGroupKey(item);
    const url = getUrlForItem(item, rootPathByLocale, defaultLocale);
    const title = stripHtmlTags(typeof item.title === 'string' ? item.title : '') || item.title;
    const mem: GroupMember = { score, url, locale, title, rankIndex };
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key)!.push(mem);
  }

  const built: Array<{ firstRank: number; group: LocalPageSearchGroup }> = [];
  for (const [groupKey, rawMembers] of groupMap) {
    const members = mergeMembersByLocale(rawMembers);
    if (members.length === 0) continue;
    const primary = pickPrimaryMember(members, uiLocale);
    const maxScore = Math.max(...members.map((m) => m.score));
    const firstRank = Math.min(...members.map((m) => m.rankIndex));
    const translations: LocalPageSearchTranslation[] = [...members]
      .sort((a, b) => compareLocalesForSearchStack(a.locale, b.locale, uiLocale))
      .map((m) => ({ locale: m.locale, url: m.url }));
    built.push({
      firstRank,
      group: {
        groupKey,
        title: primary.title,
        primaryUrl: primary.url,
        primaryLocale: primary.locale,
        translations,
        maxScore,
      },
    });
  }

  built.sort((a, b) => {
    if (b.group.maxScore !== a.group.maxScore) return b.group.maxScore - a.group.maxScore;
    if (a.firstRank !== b.firstRank) return a.firstRank - b.firstRank;
    return a.group.title.localeCompare(b.group.title, undefined, { sensitivity: 'base' });
  });

  return built.slice(0, maxGroups).map((b) => b.group);
}
